import { Modal, Form, Input, Switch, Select, Space, Button, Divider, DatePicker, Radio } from 'antd';
import { useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import type { UserData } from './config/columns/userColumns';
import PasswordChangeModal from './PasswordChangeModal';

interface UserEditModalProps {
    open: boolean;
    user: UserData | null;
    onClose: () => void;
    onSubmit: (data: any) => void;
    onPasswordChange: (userId: number, newPassword: string) => void;
    onDelete: (userId: number) => void;
}

const UserEditModal = ({
    open,
    user,
    onClose,
    onSubmit,
    onPasswordChange,
    onDelete,
}: UserEditModalProps) => {
    const [form] = Form.useForm();
    const [, setFormData] = useState<any>(null);
    const [isPwModalOpen, setIsPwModalOpen] = useState(false);
    const isSpecialist: boolean | null | undefined = Form.useWatch('isSpecialist', form);

    useEffect(() => {
        if (open && user) {
            form.setFieldsValue({
                ...user,
                birthDate: user.birthDate ? dayjs(user.birthDate) : null,
                medicalLicenseNumber: user.medicalLicenseNumber || '',
                department: user.department || '',
                position: user.position || '',
                isSpecialist: user.isSpecialist ?? null,
                specialistNumber: user.specialistNumber || '',
            });
        } else if (open) {
            form.resetFields();
        }
    }, [open, user, form]);

    const handleSubmit = () => {
        form.validateFields().then((values) => {
            const submitData = {
                userId: user?.userId,
                username: values.username,
                name: values.name,
                englishName: values.englishName,
                isSlideDownYN: values.isSlideDownYN,
                role: values.role,
                email: values.email?.trim() || undefined,
                birthDate: values.birthDate
                    ? (values.birthDate as Dayjs).format('YYYY-MM-DD')
                    : undefined,
                phone: values.phone?.trim() || undefined,
                workplace: values.workplace?.trim() || undefined,
                medicalLicenseNumber: values.medicalLicenseNumber?.trim() || undefined,
                department: values.department?.trim() || undefined,
                position: values.position?.trim() || undefined,
                // 선택하지 않았으면(기존 가입자) 보내지 않아 그대로 둔다
                isSpecialist: values.isSpecialist ?? undefined,
                specialistNumber: values.isSpecialist ? values.specialistNumber?.trim() || undefined : undefined,
            };
            setFormData(submitData);
            onSubmit(submitData);
        }).catch((info) => {
            console.log('Validate Failed:', info);
        });
    };

    const handleCancel = () => {
        form.resetFields();
        setFormData(null);
        onClose();
    };

    const handlePasswordSubmit = (newPassword: string) => {
        if (user?.userId) {
            onPasswordChange(user.userId, newPassword);
            setIsPwModalOpen(false);
        }
    };

    const handleDelete = () => {
        if (user?.userId) {
            Modal.confirm({
                title: '유저 탈퇴 처리',
                content: `'${user.name}' 유저를 탈퇴 처리하시겠습니까?`,
                okText: '탈퇴',
                okType: 'danger',
                cancelText: '취소',
                onOk: () => {
                    onDelete(user!.userId);
                    handleCancel();
                },
                style: { top: 200 }
            });
        }
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const digits = e.target.value.replace(/[^0-9]/g, '');
        let formatted = digits;
        if (digits.length <= 7) {
            formatted = digits.replace(/^(\d{3})(\d+)/, '$1-$2');
        } else {
            formatted = digits.replace(/^(\d{3})(\d{4})(\d+)/, '$1-$2-$3');
        }
        form.setFieldValue('phone', formatted);
    };

    return (
        <>
            <Modal
                title="유저 정보 수정"
                open={open}
                onCancel={handleCancel}
                centered
                width={680}
                footer={[
                    <div
                        style={{ display: 'flex', justifyContent: 'space-between' }}
                        key="footer-wrapper"
                    >
                        <Space>
                            <Button danger onClick={() => setIsPwModalOpen(true)}>
                                비밀번호 변경
                            </Button>
                            <Button danger type="primary" onClick={handleDelete}>
                                탈퇴
                            </Button>
                        </Space>
                        <Space>
                            <Button key="cancel" onClick={handleCancel}>
                                취소
                            </Button>
                            <Button key="submit" type="primary" onClick={handleSubmit}>
                                저장
                            </Button>
                        </Space>
                    </div>
                ]}
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{ isSlideDownYN: false }}
                >
                    <Divider orientation="left" plain style={{ margin: '0 0 16px', fontSize: 13, color: '#17375f' }}>
                        계정 정보
                    </Divider>

                    <FormGrid>
                        <Form.Item
                            label="이름"
                            name="name"
                            rules={[
                                { required: true, message: '이름을 입력해주세요' },
                                { whitespace: true, message: '공백만으로는 입력할 수 없습니다' },
                            ]}
                        >
                            <Input placeholder="이름을 입력하세요" />
                        </Form.Item>

                        <Form.Item
                            label="ID"
                            name="username"
                            rules={[{ required: true, message: 'ID를 입력해주세요' }]}
                        >
                            <Input placeholder="ID를 입력하세요" disabled />
                        </Form.Item>
                    </FormGrid>

                    <Form.Item
                        label="영문 이름"
                        name="englishName"
                    >
                        <Input placeholder="name" />
                    </Form.Item>

                    <Form.Item
                        label="Role"
                        name="role"
                        rules={[{ required: true, message: 'Role을 선택해주세요' }]}
                    >
                        <Select placeholder="Role을 선택하세요">
                            <Select.Option value="ADVISOR">자문위원</Select.Option>
                            <Select.Option value="SPECIAL">특별회원</Select.Option>
                            <Select.Option value="REGULAR">정회원</Select.Option>
                            <Select.Option value="GENERAL">일반회원</Select.Option>
                            <Select.Option value="STUDENT">학생회원</Select.Option>
                            <Select.Option value="OTHER">기타회원</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="슬라이드 다운로드 권한"
                        name="isSlideDownYN"
                        valuePropName="checked"
                    >
                        <Switch checkedChildren="활성" unCheckedChildren="비활성" disabled />
                    </Form.Item>

                    <Divider orientation="left" plain style={{ margin: '8px 0 16px', fontSize: 13, color: '#17375f' }}>
                        가입 정보
                    </Divider>

                    <Form.Item
                        label="이메일"
                        name="email"
                        rules={[
                            { type: 'email', message: '이메일 형식이 올바르지 않습니다' },
                        ]}
                    >
                        <Input placeholder="example@email.com" />
                    </Form.Item>

                    <FormGrid>
                        <Form.Item label="생년월일" name="birthDate">
                            <DatePicker
                                style={{ width: '100%' }}
                                format="YYYY-MM-DD"
                                placeholder="생년월일 선택"
                                disabledDate={(current) => current && current > dayjs().endOf('day')}
                            />
                        </Form.Item>

                        <Form.Item
                            label="휴대폰"
                            name="phone"
                        >
                            <Input
                                placeholder="010-0000-0000"
                                maxLength={13}
                                onChange={handlePhoneChange}
                            />
                        </Form.Item>
                    </FormGrid>

                    <Form.Item label="직장 / 학교" name="workplace">
                        <Input placeholder="소속 기관명을 입력하세요" />
                    </Form.Item>

                    <FormGrid>
                        <Form.Item label="소속과" name="department">
                            <Input placeholder="예: 병리과" maxLength={100} />
                        </Form.Item>
                        <Form.Item label="직위" name="position">
                            <Input placeholder="예: 교수, 전임의, 전공의, 학생" maxLength={100} />
                        </Form.Item>
                    </FormGrid>

                    <Form.Item
                        label="의사면허번호"
                        name="medicalLicenseNumber"
                    >
                        <Input placeholder="면허번호를 입력하세요" />
                    </Form.Item>


                    <FormGrid>
                        <Form.Item label="전문의 여부" name="isSpecialist">
                            <Radio.Group>
                                <Radio value={true}>전문의</Radio>
                                <Radio value={false}>해당 없음</Radio>
                            </Radio.Group>
                        </Form.Item>
                        {isSpecialist && (
                            <Form.Item label="전문의 번호" name="specialistNumber">
                                <Input placeholder="전문의 번호를 입력하세요" />
                            </Form.Item>
                        )}
                    </FormGrid>
                </Form>
            </Modal>

            <PasswordChangeModal
                open={isPwModalOpen}
                onClose={() => setIsPwModalOpen(false)}
                onSubmit={handlePasswordSubmit}
            />
        </>
    );
};

const FormGrid = ({ children }: { children: React.ReactNode }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {children}
    </div>
);

export default UserEditModal;